import { useState, useRef, useCallback, createContext, useEffect, memo } from 'react';
import { useAtom } from 'jotai';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  type OnConnect,
  type ReactFlowInstance,
  type NodeTypes,
  type NodeChange,
} from 'reactflow';
import { Toaster } from "@/components/ui/toaster"
import { SmartStepEdge } from '@tisoap/react-flow-smart-edge';
import type { CustomNode, CustomEdge, NodeToRenderProps, SetCustomNodes } from '@src/components/custom-nodes/types';
import type { NodesDefinition, NodesBaseConfig } from '@src/scenario-editor/render-utils/types';
import { deserealizeToNodesAndEdges, type SerializedHashTable, type ValidatorResults } from '@src/scenario-editor/utils/serializer';
import { Sidebar } from '@src/components/sidebar/Sidebar';
import { MessageInfoNode } from '@src/components/custom-nodes/MessageInfoNode';
import { IfConditionNode } from '@src/components/custom-nodes/IfConditionNode';
import { IfBranchNode } from '@src/components/custom-nodes/IfBranchNode';
import { IfCloseNode } from '@src/components/custom-nodes/IfCloseNode';
import { TimeoutNode } from '@src/components/custom-nodes/TimeoutNode';
import { PlaceholderNode } from '@src/components/custom-nodes/PlaceholderNode';
import { StartNode } from '@src/components/custom-nodes/play-node/PlayNode';
import { FinishNode } from '@src/components/custom-nodes/finish-node/FinishNode';
import { GenericNode } from '@src/components/custom-nodes/generic-node/GenericNode.tsx';
import { useDnD, useNodesChange, useEdgesChange, useInitialNodesRecalculation, useSave, useNodesHistory } from './hooks';
import { NodeInfo } from '@src/components/node-info/NodeInfo';
import { useNodeSelection, useWrapperSize, useNodesValidation } from '@src/scenario-editor/hooks';
import {
  reactFlowInstanceAtom,
  reactFlowInstanceStore,
  nodeDefinitionsAtom,
  nodeValidationResultAtom,
  nodesConfigAtom,
} from '@src/atoms';
import defaultScenario from '@src/scenario-editor/empty-scenario-mock.json';
// INFO: uncomment for local dev
import { nodeItemsConfig } from '@src/scenario-editor/default-items-config';
import { nodeDefinitions } from '@src/scenario-editor/default-items-definition';
import { ROOT_WRAPPER_CLASS } from '@src/consts.ts';
import cn from 'classnames';
import { ReactflowInstanceProvider } from '@src/scenario-editor/providers/editor-instance.context';
import { HistoryProvider } from '@src/scenario-editor/providers/history.context';
import {
  FetchProvider,
  type FetchMessagesPayload,
  type FetchAllMessagesPayload,
  type FetchOptionsByMessagePayload,
  type FetchDynamicItemsPayload,
  type FetchFilteredDynamicItemsPayload,
  type FetchArticleReferencesPayload,
  type FetchArticlePreviewPayload,
  type PickedItem,
  type FetchAllScenarioPayload,
  type FetchTaskManagementsPayload,
  type FetchProfileManagementPayload,
  type FetchOnboardingQuestionsPayload,
  type UploadImageParams,
  type UploadImagePayload,
  type FetchImageUrlByIdPayload,
  type FetchImageContentByIdPayload,
} from '@src/scenario-editor/providers/fetch.context';
import { fetchStubs } from '@src/scenario-editor/stubs/fetchers';
import { BehavioralRulesDataProvider } from '@src/components/node-info/providers/behavioral-rules-data.provider';
import { onChange } from '@src/scenario-editor/utils/detect-change';
import { ErrorBoundary } from '@src/components/error-boundary/ErrorBoundary';

const nodeTypes = {
  MessageInfoNode,
  PlaceholderNode,
  IfConditionNode,
  IfConditionBehaviorNode: IfConditionNode,
  IfBranchNode,
  TimeoutNode,
  StartNode,
  FinishNode,
  IfCloseNode,
  GenericNode,
};

const edgeTypes = {
  smartStep: SmartStepEdge,
}

export const ReactflowInstanceContext = createContext<typeof reactFlowInstanceStore>(reactFlowInstanceStore);

interface ScenarioEditorWrapperProps {
  id?: string;
  defaultValue?: SerializedHashTable;
  nodesDefinition?: NodesDefinition;
  nodesConfig?: NodesBaseConfig[];
  disabled?: boolean;
  itemType?: string;
  fetchers?: {
    fetchAlternatives?: () => Promise<FetchMessagesPayload>;
    fetchAllMessages?: () => Promise<FetchAllMessagesPayload>;
    fetchOptionsByMessage?: () => Promise<FetchOptionsByMessagePayload>;
    fetchFitFacts?: () => Promise<FetchDynamicItemsPayload>;
    fetchFilteredFitFacts?: () => Promise<FetchFilteredDynamicItemsPayload>;
    fetchArticles?: () => Promise<FetchDynamicItemsPayload>
    fetchFilteredArticles?: () => Promise<FetchFilteredDynamicItemsPayload>;
    fetchArticleReferences?: (ids: string[]) => Promise<FetchArticleReferencesPayload>;
    fetchArticlePreview?: (id: string) => Promise<FetchArticlePreviewPayload>;
    pickArticles?: (selectedIds: string[]) => Promise<PickedItem[] | null>;
    pickFitFacts?: (selectedIds: string[]) => Promise<PickedItem[] | null>;
    fetchAllScenario?: () => Promise<FetchAllScenarioPayload>;
    fetchTaskManagements?: () => Promise<FetchTaskManagementsPayload>;
    fetchProfileManagement?: () => Promise<FetchProfileManagementPayload>;
    fetchOnboardingQuestions?: () => Promise<FetchOnboardingQuestionsPayload>;
    uploadImage?: (params: UploadImageParams) => Promise<UploadImagePayload>;
    fetchImageUrlById?: (ids: string[]) => Promise<FetchImageUrlByIdPayload>;
    fetchImageContentById?: (ids: string[]) => Promise<FetchImageContentByIdPayload>;
  }
  onChange?: (payload: SerializedHashTable, validatorResults: ValidatorResults) => void;
  isPreview?: boolean;
}
const Editor = memo(function Editor({
  id,
  onChange,
  nodesConfig,
  nodesDefinition,
  disabled,
  isPreview,
  defaultValue = defaultScenario
}: ScenarioEditorWrapperProps) {
  const reactFlowWrapper = useRef(null);

  const [, setNodeDefinitionsList] = useAtom(nodeDefinitionsAtom);
  const [, setNodesConfig] = useAtom(nodesConfigAtom);
  const [, setNodeValidationResults] = useAtom(nodeValidationResultAtom);
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance<ReactFlowInstance, unknown>>();
  const history = useNodesHistory({ key: id ?? '' });

  useEffect(() => {
    if (reactFlowInstance && nodesDefinition) {
      const { nodes, edges } = deserealizeToNodesAndEdges(defaultValue, nodesDefinition);
      setNodes(nodes);
      setEdges(edges);
    }
  }, [reactFlowInstance, defaultValue, nodesDefinition]);

  useEffect(() => {
    if (history?.history.length > 0 && history.cursor > -1 && nodesDefinition) {
      const serializedState = history.history[history.cursor];
      if (serializedState) {
        const {nodes, edges} = deserealizeToNodesAndEdges(history.history[history.cursor], nodesDefinition)
        console.log('de-serialisation from history', history);
        setNodes(nodes);
        setEdges(edges);
      }
    }
  }, [history.cursor, nodesDefinition])

  useEffect(() => {
    if (nodesDefinition) {
      setNodeDefinitionsList(nodesDefinition);
    }
  }, [nodesDefinition, setNodeDefinitionsList]);

  useEffect(() => {
    if (nodesConfig) {
      setNodesConfig(nodesConfig);
    }
  }, [nodesConfig, setNodesConfig]);

  const onConnect: OnConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [],
  );
  const { elementsSelectable, ...dndProps } = useDnD({
    reactFlowWrapper,
    reactFlowInstance,
  });
  const { onNodesChange } = useNodesChange({ nodes, edges, setNodes, setEdges });
  const { onEdgesChange } = useEdgesChange({ nodes, edges, setEdges });

  useInitialNodesRecalculation({
    nodes: nodes as CustomNode[],
    setNodes: setNodes as SetCustomNodes,
    reactFlowWrapper,
    reactFlowInstance,
  });
  const { onNodeClick, onPaneClick } = useNodeSelection();
  useWrapperSize(reactFlowWrapper);
  useNodesValidation(reactFlowInstance, nodes as CustomNode[]);
  useSave(nodes as CustomNode[], edges as CustomEdge[], (...args) => {
    onChange?.(...args);
    setNodeValidationResults(args[1]);
  });

  console.log('history', history.history);

  const initHandler = (instance: ReactFlowInstance) => {
    setReactFlowInstance(instance);
    reactFlowInstanceStore.set(reactFlowInstanceAtom, instance);
  }

  const handleNodesChange = (nodeChanges: NodeChange[]) => {
    onNodesChange(nodeChanges);
  }

  console.log('nodes', nodes)
  console.log('disabled', disabled)

  return (
    <ReactflowInstanceProvider>
      <div className="editor-wrapper bg-background flex w-full h-full">
        <div className={cn([ROOT_WRAPPER_CLASS, 'w-screen h-full'])} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={initHandler}
            nodeTypes={nodeTypes as unknown as NodeTypes}
            edgeTypes={edgeTypes}
            snapGrid={[15, 15]}
            nodesDraggable={false}
            snapToGrid
            zoomOnPinch
            zoomOnScroll={false}
            panOnDrag={false}
            panOnScroll
            elementsSelectable={elementsSelectable}
            className="bg-background"
            edgesUpdatable={!disabled}
            edgesFocusable={!disabled}
            nodesConnectable={!disabled}
            nodesFocusable={!disabled}
            draggable={false}
            {...dndProps}
            onNodeClick={(event, node) => onNodeClick(event, node as NodeToRenderProps)}
            onPaneClick={onPaneClick}
          >
            <Controls/>
          </ReactFlow>
        </div>
        {!isPreview && (
          <Sidebar nodesConfig={nodesConfig} nodesDefinition={nodesDefinition}/>
        )}
        <NodeInfo isPreview={isPreview ?? false} />
        <Toaster />
      </div>
    </ReactflowInstanceProvider>
  );
}, (prevProps, nextProps) =>
  prevProps.defaultValue === nextProps.defaultValue
  && prevProps.nodesConfig === nextProps.nodesConfig
  && prevProps.nodesDefinition === nextProps.nodesDefinition
  && prevProps.disabled === nextProps.disabled
  && prevProps.onChange === nextProps.onChange
);

const onChangeHandler = (value: SerializedHashTable, validatorResults: ValidatorResults) => {
  console.log('>>>onChange', value, validatorResults);
  onChange(value);
}

const ScenarioEditorWrapper = ({
  defaultValue,
  // INFO: uncomment for local dev
  nodesConfig = nodeItemsConfig,
  nodesDefinition = nodeDefinitions as NodesDefinition,
  itemType = 'scenario',
  disabled,
  isPreview,
  onChange = (value, validatorResults) => {
    onChangeHandler(value, validatorResults);
  },
  fetchers: {
    fetchAlternatives,
    fetchAllMessages,
    fetchOptionsByMessage,
    fetchFitFacts,
    fetchFilteredFitFacts,
    fetchArticles,
    fetchFilteredArticles,
    fetchArticleReferences,
    fetchArticlePreview,
    pickArticles,
    pickFitFacts,
    fetchAllScenario,
    fetchTaskManagements,
    fetchProfileManagement,
    fetchOnboardingQuestions,
    uploadImage,
    fetchImageUrlById,
    fetchImageContentById,
  }={
    ...fetchStubs,
  },
  id
}: ScenarioEditorWrapperProps) => (
  <ErrorBoundary>
    <ReactFlowProvider>
      <HistoryProvider>
        <FetchProvider
          fetchers={{
            fetchAlternatives: fetchAlternatives ?? fetchStubs.fetchAlternatives,
            fetchAllMessages: fetchAllMessages ?? fetchStubs.fetchAllMessages,
            fetchOptionsByMessage: fetchOptionsByMessage ?? fetchStubs.fetchOptionsByMessage,
            fetchFitFacts: fetchFitFacts ?? fetchStubs.fetchFitFacts,
            fetchFilteredFitFacts: fetchFilteredFitFacts ?? fetchStubs.fetchFilteredFitFacts,
            fetchArticles: fetchArticles ?? fetchStubs.fetchArticles,
            fetchFilteredArticles: fetchFilteredArticles ?? fetchStubs.fetchFilteredArticles,
            // LF360-2023: no stub fallback - a consumer that does not pass it keeps today's behaviour.
            fetchArticleReferences,
            fetchArticlePreview,
            // LF360-2030: no stub either - without them the nodes keep the list picker.
            pickArticles,
            pickFitFacts,
            fetchAllScenario: fetchAllScenario ?? fetchStubs.fetchAllScenario,
            fetchTaskManagements: fetchTaskManagements ?? fetchStubs.fetchTaskManagements,
            fetchProfileManagement: fetchProfileManagement ?? fetchStubs.fetchProfileManagement,
            fetchOnboardingQuestions: fetchOnboardingQuestions ?? fetchStubs.fetchOnboardingQuestions,
            uploadImage: (params: Omit<UploadImageParams, 'sourceId' | 'sourceType'>) => {
              const uploader = uploadImage ?? fetchStubs.uploadImage;
              return uploader({ ...params, sourceId: id ?? '', sourceType: itemType });
            },
            fetchImageUrlById: fetchImageUrlById ?? fetchStubs.fetchImageUrlById,
            fetchImageContentById: fetchImageContentById ?? fetchStubs.fetchImageContentById,
          }}
        >
          <BehavioralRulesDataProvider>
            <Editor
              defaultValue={defaultValue}
              nodesConfig={nodesConfig}
              nodesDefinition={nodesDefinition}
              onChange={onChange}
              id={id}
              disabled={disabled}
              isPreview={isPreview}
              fetchers={{
                fetchAlternatives,
                fetchAllMessages,
                fetchOptionsByMessage,
                fetchFitFacts,
                fetchFilteredFitFacts,
                fetchAllScenario,
                fetchArticles,
                fetchFilteredArticles,
                fetchTaskManagements,
                uploadImage,
                fetchImageUrlById,
                fetchImageContentById,
            }}
            />
          </BehavioralRulesDataProvider>
        </FetchProvider>
      </HistoryProvider>
    </ReactFlowProvider>
  </ErrorBoundary>
);

export default ScenarioEditorWrapper;
